<template>
    <AuthenticatedShell full-bleed-brand>
        <div class="ea-config-layout">
            <div class="ea-config-scroll">
                <div class="wizard agents-list-page">
                    <header class="agents-list-head">
                        <div>
                            <h1 class="agents-list-title">Todos os agentes</h1>
                            <p class="agents-list-lead">
                                Clique numa linha para ir ao chat com o painel lateral de parâmetros aberto (pode ajustar
                                e tocar em <strong>Enviar</strong> para refazer o pedido). Use
                                <strong>Parâmetros</strong> só para editar na lista, sem mudar de página. CRUD (criar,
                                editar, arquivar) será ligado à API em seguida.
                            </p>
                        </div>
                        <div class="agents-list-head-actions">
                            <router-link class="btn-ghost" to="/tela-principal">← Voltar ao início</router-link>
                            <button type="button" class="btn-primary" disabled title="Em breve">Novo agente</button>
                        </div>
                    </header>

                    <div class="agents-list-toolbar">
                        <input
                            v-model.trim="searchQuery"
                            type="search"
                            class="agents-search"
                            placeholder="Buscar por nome ou descrição…"
                            aria-label="Buscar agentes"
                        />
                        <span class="agents-count-pill">{{ filteredRows.length }} agentes</span>
                    </div>

                    <div class="agents-table-wrap">
                        <table class="agents-table">
                            <thead>
                                <tr>
                                    <th scope="col">Agente</th>
                                    <th scope="col" class="th-narrow">Tipo</th>
                                    <th scope="col">Descrição</th>
                                    <th scope="col" class="th-actions">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="crew in filteredRows"
                                    :key="crew.id"
                                    class="agents-row agents-row--clickable"
                                    tabindex="0"
                                    role="button"
                                    :aria-label="'Abrir chat com o agente ' + (crew.name || crew.id)"
                                    @click="startChatWithCrew(crew)"
                                    @keyup.enter="startChatWithCrew(crew)"
                                >
                                    <td>
                                        <div class="td-name">
                                            <span class="td-title">{{ crew.name }}</span>
                                            <span class="td-id">#{{ crew.id }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="agent-badge" :data-tone="inferCategory(crew).tone">
                                            {{ inferCategory(crew).label }}
                                        </span>
                                    </td>
                                    <td class="td-desc">{{ excerpt(crew.description, 200) }}</td>
                                    <td class="td-actions" @click.stop>
                                        <button type="button" class="btn-row primary" @click="openAgent(crew)">
                                            Parâmetros
                                        </button>
                                        <button type="button" class="btn-row" disabled title="Em breve">Editar</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-if="!loading && filteredRows.length === 0" class="agents-empty">Nenhum agente encontrado.</p>
                    </div>
                    <p v-if="loading" class="agents-loading">Carregando…</p>
                </div>
            </div>
        </div>
    </AuthenticatedShell>
</template>

<script>
import AuthenticatedShell from '@/components/layout/AuthenticatedShell.vue';
import { mapState, mapActions } from 'vuex';
import crews from '@/utils/crews';
import parameters from '@/utils/parameters.js';
import { inferAgentCategory, excerpt } from '@/utils/agentDisplay';

export default {
    name: 'EscoAgentesLista',
    components: { AuthenticatedShell },
    data() {
        return {
            localCrews: [],
            searchQuery: '',
            loading: false
        };
    },
    computed: {
        ...mapState(['token', 'crewsList']),
        rows() {
            return this.crewsList && this.crewsList.length ? this.crewsList : this.localCrews;
        },
        filteredRows() {
            const q = this.searchQuery.toLowerCase();
            if (!q) return this.rows;
            return this.rows.filter(c => {
                const hay = `${c.name || ''} ${c.description || ''}`.toLowerCase();
                return hay.includes(q);
            });
        }
    },
    mounted() {
        this.bootstrap();
    },
    methods: {
        ...mapActions([
            'storeIsVisibleSideBarGenericModal',
            'storeFieldsModalGeneric',
            'storeCurrentCrew',
            'storeCurrentRunIa',
            'storeCrewsList'
        ]),
        inferCategory: inferAgentCategory,
        excerpt,
        async bootstrap() {
            if (this.crewsList && this.crewsList.length) {
                this.localCrews = [...this.crewsList];
                return;
            }
            if (!this.token) return;
            try {
                this.loading = true;
                const response = await crews.getCrews(this.token);
                this.localCrews = response;
                this.storeCrewsList({ crewsList: response });
            } catch (e) {
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        /** Valor inicial por campo (igual ao modal antes de o utilizador editar). */
        valueFromParameterField(field) {
            const v = field.value;
            if (v !== undefined && v !== null && v !== '') return v;
            if (field.default !== undefined && field.default !== null && field.default !== '') return field.default;
            return field.type === 6 ? false : '';
        },
        /**
         * Abre /chat com o mesmo payload que “Enviar” no painel de parâmetros (modalGeneric.handleSave).
         */
        async startChatWithCrew(crew) {
            if (!this.token) return;
            try {
                const fields = await parameters.getParameters(this.token, crew.id);
                const runPayload = {
                    id: 0,
                    type_engine: 1,
                    crew_id: crew.id,
                    request_type: 1,
                    status: 0,
                    parameters: fields.map(field => ({
                        type: 1,
                        property: field.property,
                        value: this.valueFromParameterField(field)
                    }))
                };
                await this.storeCurrentCrew({ currentCrew: crew });
                await this.storeFieldsModalGeneric({ fields });
                await this.storeCurrentRunIa({ currentRunIa: runPayload });
                await this.storeIsVisibleSideBarGenericModal({ isVisibleSideBarGenericModal: true });
                this.$router.push('/chat');
            } catch (e) {
                console.error(e);
            }
        },
        async openAgent(crew) {
            if (!this.token) return;
            try {
                const response = await parameters.getParameters(this.token, crew.id);
                this.storeIsVisibleSideBarGenericModal({ isVisibleSideBarGenericModal: true });
                this.storeFieldsModalGeneric({ fields: response });
                this.storeCurrentCrew({ currentCrew: crew });
            } catch (e) {
                console.error(e);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.ea-config-layout {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
}

.ea-config-scroll {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    padding: 0.5rem clamp(0.75rem, 2.5vw, 1.5rem) 1.25rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
}
.ea-config-scroll::-webkit-scrollbar {
    width: 0;
    height: 0;
}

.agents-list-page {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    padding: 0.35rem 0 2rem;
    width: 100%;
    max-width: none;
    margin: 0;
}

.agents-list-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.agents-list-title {
    margin: 0 0 0.35rem;
    font-size: clamp(1.35rem, 2.4vw, 1.75rem);
    font-weight: 700;
    color: #f2f3f5;
}

.agents-list-lead {
    margin: 0;
    max-width: 42rem;
    color: #9aa3b2;
    font-size: 14px;
    line-height: 1.55;
}

.agents-list-head-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
}

.btn-ghost {
    padding: 0.45rem 0.9rem;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: transparent;
    color: #c8cdd8;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
}
.btn-ghost:hover {
    border-color: rgba(206, 255, 31, 0.35);
    color: #fff;
}

.btn-primary {
    padding: 0.45rem 1.1rem;
    border-radius: 10px;
    border: none;
    background: rgba(206, 255, 31, 0.22);
    color: #dff565;
    font-size: 14px;
    font-weight: 600;
    cursor: not-allowed;
    opacity: 0.65;
}

.agents-list-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.agents-search {
    flex: 1 1 220px;
    min-width: 0;
    padding: 0.55rem 0.85rem;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: #1c1d22;
    color: #fff;
    font-size: 14px;
}
.agents-search::placeholder {
    color: #6b7280;
}
.agents-search:focus {
    outline: none;
    border-color: rgba(206, 255, 31, 0.45);
    box-shadow: 0 0 0 2px rgba(206, 255, 31, 0.12);
}

.agents-count-pill {
    font-size: 13px;
    color: #9aa3b2;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(0, 0, 0, 0.2);
}

.agents-table-wrap {
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: #15161a;
    overflow: auto;
    flex: 1 1 auto;
    min-height: 0;
}

.agents-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.agents-table th {
    text-align: left;
    padding: 0.75rem 1rem;
    color: #8b9099;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(0, 0, 0, 0.25);
}
.agents-table td {
    padding: 0.85rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: top;
    color: #e8eaef;
}
.agents-table tr:last-child td {
    border-bottom: none;
}

.agents-row--clickable {
    cursor: pointer;
    transition: background 0.12s ease;
}
.agents-row--clickable:hover {
    background: rgba(255, 255, 255, 0.04);
}
.agents-row--clickable:focus-visible {
    outline: 2px solid rgba(206, 255, 31, 0.42);
    outline-offset: -2px;
}

.th-narrow {
    width: 8.5rem;
}
.th-actions {
    width: 11rem;
    text-align: right;
}
.td-name {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}
.td-title {
    font-weight: 600;
    color: #fff;
}
.td-id {
    font-size: 12px;
    color: #6b7280;
}
.td-desc {
    color: #b4bcc8;
    line-height: 1.45;
}
.td-actions {
    text-align: right;
    white-space: nowrap;
}
.btn-row {
    padding: 0.35rem 0.65rem;
    margin-left: 0.35rem;
    border-radius: 8px;
    font-size: 13px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: transparent;
    color: #c8cdd8;
    cursor: pointer;
}
.btn-row:hover:not(:disabled) {
    border-color: rgba(206, 255, 31, 0.35);
    color: #fff;
}
.btn-row:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
.btn-row.primary {
    border-color: rgba(206, 255, 31, 0.35);
    background: rgba(206, 255, 31, 0.1);
    color: #dff565;
}

.agent-badge {
    display: inline-block;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: 1px solid transparent;
}
.agent-badge[data-tone='blue'] {
    color: #7dd3fc;
    background: rgba(56, 189, 248, 0.12);
    border-color: rgba(56, 189, 248, 0.25);
}
.agent-badge[data-tone='amber'] {
    color: #fcd34d;
    background: rgba(251, 191, 36, 0.1);
    border-color: rgba(251, 191, 36, 0.25);
}
.agent-badge[data-tone='mint'] {
    color: #86efac;
    background: rgba(74, 222, 128, 0.1);
    border-color: rgba(74, 222, 128, 0.22);
}
.agent-badge[data-tone='cyan'] {
    color: #67e8f9;
    background: rgba(34, 211, 238, 0.1);
    border-color: rgba(34, 211, 238, 0.22);
}
.agent-badge[data-tone='slate'] {
    color: #cbd5e1;
    background: rgba(148, 163, 184, 0.12);
    border-color: rgba(148, 163, 184, 0.2);
}

.agents-empty,
.agents-loading {
    margin: 0;
    padding: 1.25rem 1rem;
    text-align: center;
    color: #8b9099;
    font-size: 14px;
}
</style>
